import React, { useState, useEffect, useMemo, useContext, useRef, useCallback } from "react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { useNavigate } from "react-router-dom";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { Spinner } from "reactstrap";
import axios from "axios";
import { _imagesURL, adminBaseURL } from "../../constants/API";
import { LOGIN, NEW_CONTEST } from "../../constants/RoutesUrl";
import { Language, Sponsorpost } from "../../App";
import "./AgendaPromoBanner.css";
import "./AgendaFoundation.css";

const DEFAULT_ASPECT_RATIO = 1.45;

function getAdHeight(slide, width) {
  const size = parseFloat(slide?.media_size);
  const ratio = Number.isFinite(size) && size > 0 ? size : DEFAULT_ASPECT_RATIO;
  const safeWidth = width > 0 ? width : 340;
  if (safeWidth > 480) {
    return Math.min(260, Math.max(160, Math.round(safeWidth / Math.max(ratio, 3.2))));
  }
  return Math.round(safeWidth / ratio);
}

function AgendaPromoSlide({ slide, currentHeight, isClickable, onAdClick }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <div
      className={`agenda-ad-slide position-relative d-flex align-items-center justify-content-center ${isClickable ? "is-clickable" : ""
        }`}
      style={{
        width: "100%",
        height: currentHeight ? `${currentHeight}px` : "240px",
        cursor: isClickable ? "pointer" : "default",
        background: "#F4F7FB",
        position: "relative",
        overflow: "hidden",
      }}
      onClick={() => onAdClick(slide)}
      aria-label={slide.title}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: loaded ? 0 : 1,
          transition: "opacity 0.4s ease",
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        <Spinner style={{ color: "#1dc1dd", width: "8px", height: "8px", borderWidth: "1.5px" }} />
      </div>
      {slide.media && (
        <img
          src={slide.media}
          alt={slide.title || "Advertisement"}
          className="agenda-ad-img object-cover"
          onLoad={() => setLoaded(true)}
          onError={(e) => {
            e.target.onerror = null;
            e.target.style.display = "none";
          }}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: loaded ? 1 : 0,
            transition: "opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1), transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
            willChange: "opacity, transform",
          }}
        />
      )}
    </div>
  );
}

export function AgendaSidebarAdShimmer({ height = 240 }) {
  return (
    <div className="agenda-sidebar-ad-card agenda-sidebar-ad-shimmer" aria-hidden="true">
      {/* Image / Picture area */}
      <div
        className="agenda-shimmer-block"
        style={{ width: "100%", height: `${height}px`, borderRadius: 0 }}
      />
      {/* Bottom dot-nav bar */}
      <div className="agenda-ad-shimmer-bar">
        <div className="agenda-ad-shimmer-circle agenda-shimmer-block" />
        <div className="agenda-ad-shimmer-pill agenda-shimmer-block" />
        <div className="agenda-ad-shimmer-circle agenda-shimmer-block" />
      </div>
    </div>
  );
}

export default function AgendaPromoBanner({
  ads = [],
  loading = false,
  promo,
}) {
  const navigate = useNavigate();
  const sponsorContext = useContext(Sponsorpost);
  const updateContext = sponsorContext?.updateContext;
  const { lang } = useContext(Language);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);
  const carouselRef = useRef(null);

  // Measure container width dynamically to adapt ad height to aspect ratio
  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(containerRef.current);
    window.addEventListener("resize", updateWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  // Normalize and flatten ads into standard slide format
  const slides = useMemo(() => {
    const rawList = Array.isArray(ads)
      ? ads
      : ads?.agenda_left && Array.isArray(ads.agenda_left)
        ? ads.agenda_left
        : [];

    if (rawList.length === 0) {
      return [];
    }

    const flatSlides = [];

    rawList.forEach((item, itemIdx) => {
      const ad = item?.general_ad || item;
      if (!ad) return;

      const dynamicImages = ad?.dynamic_ad?.images;

      // Multi-image dynamic ad
      if (Array.isArray(dynamicImages) && dynamicImages.length > 0) {
        dynamicImages.forEach((img, imgIdx) => {
          let mediaUrl = img.media || ad.media || "";
          if (
            mediaUrl &&
            !mediaUrl.startsWith("http://") &&
            !mediaUrl.startsWith("https://")
          ) {
            mediaUrl = `${_imagesURL}${mediaUrl}`;
          }

          const parsedSize = parseFloat(img.media_size || ad.media_size);
          const ratio =
            Number.isFinite(parsedSize) && parsedSize > 0
              ? parsedSize
              : DEFAULT_ASPECT_RATIO;

          if (mediaUrl) {
            flatSlides.push({
              id: `ad-${ad.id || itemIdx}-img-${img.id || imgIdx}`,
              media: mediaUrl,
              media_size: ratio,
              title: img.title || ad.name || "Advertisement",
              description: img.description || "",
              btnText: img.btn_text || "",
              btnTextSpanish: img.btn_text_spanish || "",
              link: img.link || ad.link || "",
              type: ad.type || "dynamic",
              internal_model: ad.internal_model,
              rawAd: ad,
              rawImage: img,
              isDefault: false,
            });
          }
        });
      } else {
        // Single ad
        let mediaUrl = ad.media || "";
        if (
          mediaUrl &&
          !mediaUrl.startsWith("http://") &&
          !mediaUrl.startsWith("https://")
        ) {
          mediaUrl = `${_imagesURL}${mediaUrl}`;
        }

        const parsedSize = parseFloat(ad.media_size);
        const ratio =
          Number.isFinite(parsedSize) && parsedSize > 0
            ? parsedSize
            : DEFAULT_ASPECT_RATIO;

        if (mediaUrl) {
          flatSlides.push({
            id: `ad-${ad.id || itemIdx}`,
            media: mediaUrl,
            media_size: ratio,
            title: ad.name || "Advertisement",
            link: ad.link || "",
            type: ad.type,
            internal_model: ad.internal_model,
            rawAd: ad,
            isDefault: false,
          });
        }
      }
    });

    return flatSlides;
  }, [ads]);

  // Reset slide index if slides change and index is out of bounds
  useEffect(() => {
    if (currentSlide >= slides.length) {
      setCurrentSlide(0);
    }
  }, [slides.length, currentSlide]);

  // Handle ad navigation / interactions
  const handleAdClick = useCallback(
    (slide) => {
      if (!slide || slide.isDefault) return;
      const ad = slide.rawAd;
      const targetLink = slide.link || ad?.link;

      if (
        ad?.type === "external" ||
        (targetLink &&
          (targetLink.startsWith("http://") ||
            targetLink.startsWith("https://") ||
            targetLink.includes("www.")))
      ) {
        const url =
          targetLink.startsWith("http://") || targetLink.startsWith("https://")
            ? targetLink
            : `https://${targetLink}`;
        window.open(url, "_blank", "noopener,noreferrer");
      } else if (ad?.type === "internal") {
        switch (ad.internal_model) {
          case "course":
            if (ad.seo_slug || ad.course_id) navigate(`/course/${ad.seo_slug || ad.course_id}`);
            break;
          case "profile":
            if (ad.user_name || ad.user_id) navigate(`/profile/${ad.user_name || ad.user_id}`);
            break;
          case "clinical_session":
            if (ad.post_id) navigate(`/session/${ad.post_id}`);
            break;
          case "webinar":
            if (ad.seo_slug || ad.post_id) navigate(`/webinar/${ad.seo_slug || ad.post_id}`);
            break;
          case "clinical_study":
            if (ad.post_id) navigate(`/post/${ad.post_id}`);
            break;
          case "event":
            if (ad.seo_slug || ad.event_id) navigate(`/event/${ad.seo_slug || ad.event_id}`);
            break;
          default:
            break;
        }
      } else {
        // dynamic contest / dynamic ad
        navigate(NEW_CONTEST, { state: { dynamicAd: ad } });
      }

      if (typeof updateContext === "function" && ad) {
        updateContext(ad);
      }

      // Tracking analytics call
      try {
        const signupData = JSON.parse(
          localStorage.getItem("SignupData") || "{}"
        );
        const token = signupData?.data?.token;
        if (token && ad?.id) {
          axios
            .post(
              `${adminBaseURL}user/ads/view`,
              { device: "Web", ad_id: ad.id, type: "view" },
              { headers: { Authorization: `Bearer ${token}` } }
            )
            .catch(() => { });
        }
      } catch (err) {
        // Analytics non-blocking
      }
    },
    [navigate, updateContext]
  );

  const totalSlides = slides.length;

  const handlePrev = useCallback(
    (e) => {
      e?.stopPropagation?.();
      const prevIdx = currentSlide === 0 ? totalSlides - 1 : currentSlide - 1;
      setCurrentSlide(prevIdx);
      carouselRef.current?.moveTo?.(prevIdx);
    },
    [currentSlide, totalSlides]
  );

  const handleNext = useCallback(
    (e) => {
      e?.stopPropagation?.();
      const nextIdx = currentSlide === totalSlides - 1 ? 0 : currentSlide + 1;
      setCurrentSlide(nextIdx);
      carouselRef.current?.moveTo?.(nextIdx);
    },
    [currentSlide, totalSlides]
  );

  const handleDotClick = useCallback((idx) => {
    setCurrentSlide(idx);
    carouselRef.current?.moveTo?.(idx);
  }, []);

  if (loading) {
    return <AgendaSidebarAdShimmer />;
  }

  if (slides.length === 0) {
    return null;
  }

  const firstSlide = slides[0];
  const currentHeight = getAdHeight(firstSlide, containerWidth);

  return (
    <div className="agenda-sidebar-ad-card" ref={containerRef}>
      {/* ── Media Area ── */}
      <div
        className="agenda-ad-media-wrap"
        style={{
          height: currentHeight ? `${currentHeight}px` : "240px",
        }}
      >
        {/* Carousel */}
        <Carousel
          ref={carouselRef}
          showArrows={false}
          showStatus={false}
          showThumbs={false}
          showIndicators={false}
          infiniteLoop={totalSlides > 1}
          autoPlay={totalSlides > 1}
          interval={4500}
          selectedItem={currentSlide}
          onChange={(idx) => setCurrentSlide(idx)}
          swipeable={totalSlides > 1}
          emulateTouch={totalSlides > 1}
        >
          {slides.map((slide, index) => {
            const isClickable =
              !slide.isDefault &&
              (Boolean(slide.link) ||
                slide.rawAd?.type === "dynamic" ||
                slide.rawAd?.type === "internal" ||
                slide.rawAd?.type === "external");

            return (
              <AgendaPromoSlide
                key={slide.id || index}
                slide={slide}
                currentHeight={currentHeight}
                isClickable={isClickable}
                onAdClick={handleAdClick}
              />
            );
          })}
        </Carousel>
      </div>

      {/* ── Bottom Navigation Controls Bar ── */}
      {totalSlides > 1 && (
        <div className="agenda-ad-bottom-bar">
          {/* Previous Button */}
          <button
            type="button"
            className="agenda-ad-bar-btn prev"
            onClick={handlePrev}
            aria-label="Previous advertisement"
          >
            <FiChevronLeft
              size={15}
              strokeWidth={2.4}
              style={{ transform: "translateX(-1px)" }}
            />
          </button>

          {/* Dot Indicator Strip */}
          <div className="agenda-ad-dots-wrap">
            {totalSlides <= 8 ? (
              slides.map((_, idx) => (
                <div
                  key={idx}
                  className="agenda-ad-dot"
                  style={{
                    width: idx === currentSlide ? "20px" : "6px",
                    background:
                      idx === currentSlide
                        ? "linear-gradient(135deg, #00BADA 0%, #0099B8 100%)"
                        : "#D0DDE8",
                  }}
                  onClick={() => handleDotClick(idx)}
                />
              ))
            ) : (
              <span className="agenda-ad-counter-text">
                {currentSlide + 1} / {totalSlides}
              </span>
            )}
          </div>

          {/* Next Button */}
          <button
            type="button"
            className="agenda-ad-bar-btn next"
            onClick={handleNext}
            aria-label="Next advertisement"
          >
            <FiChevronRight
              size={15}
              strokeWidth={2.4}
              style={{ transform: "translateX(1px)" }}
            />
          </button>
        </div>
      )}
    </div>
  );
}
